import React, { useRef } from 'react';
import { Box, Typography, Container, Button, Stack } from '@mui/material';
import logo from '../logos/png/White logo - no background.png';
import Features from '../components/Features';
import AboutUs from '../components/AboutUs';
import Quote from '../components/Quote';

const LandingPage: React.FC = () => {
  const featuresSectionRef = useRef<HTMLDivElement>(null);
  const aboutUsSectionRef = useRef<HTMLDivElement>(null);
  const quotesSection = useRef<HTMLDivElement>(null);


  const scrollToFeatures = () => {
    featuresSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToAboutUs = () => {
    aboutUsSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToQuotesSection = () => {
    quotesSection.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #e0e0e0 0%, #e0e0e0 50%, #e0e0e0 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)',
          zIndex: 1,
        },
      }}
    >
      {/* Hero Section with background image and overlay */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: '100vh',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        {/* Background image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/images/technology.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 1,
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,40,0.65)',
            zIndex: 2,
          }}
        />
        {/* Logo */}
        <Container 
          maxWidth="lg" 
          sx={{ 
            position: 'relative', 
            zIndex: 3, 
            mt: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="Preciora Logo"
            sx={{
              width: { xs: '150px', md: '200px' },
              height: 'auto',
              filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.3))',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
        </Container>
        {/* Hero Content */}
        <Container maxWidth="md" sx={{
          position: 'relative',
          zIndex: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          pb: 8,
        }}>
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 400,
              letterSpacing: '0.05em',
              mb: 8,
              fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
              textTransform: 'none',
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
              textAlign: 'center',
              textShadow: '0 2px 16px rgba(0,0,0,0.7)',
            }}
          >
            Transforming technology for your business and imparts Tech Education
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={5}>
            <Button
              variant="contained"
              size="large"
              onClick={scrollToFeatures}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.25rem',
                borderRadius: 3,
                background: 'linear-gradient(45deg, rgba(0,0,80,0.7) 0%, rgba(0,0,80,0.9) 100%)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(0,0,80,0.4)',
                color: '#fff',
                transition: 'all 0.3s ease',
                fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                '&:hover': {
                  background: 'linear-gradient(45deg, rgba(0,0,80,0.9) 0%, rgba(0,0,80,1) 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  border: '2px solid rgba(0,0,80,0.6)',
                },
                '&:active': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                },
              }}
            >
              Get Started Now
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={scrollToAboutUs}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.25rem',
                borderRadius: 3,
                background: 'transparent',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255,255,255,0.7)',
                color: '#fff',
                transition: 'all 0.3s ease',
                fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                '&:hover': {
                  background: 'rgba(0,0,80,0.1)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  border: '2px solid rgba(255,255,255,1)',
                },
                '&:active': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                },
              }}
            >
              About Us
            </Button>
          </Stack>
        </Container>
      </Box>

      <Features sectionRef={featuresSectionRef} />
      <Quote sectionRef={quotesSection} />
      <AboutUs sectionRef={aboutUsSectionRef} />
    </Box>
  );
};

export default LandingPage; 